import React, { useState, useEffect, useCallback } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  Typography,
  useTheme,
  Box,
  Button,
  CircularProgress,
  Switch,
  InputAdornment,
} from "@mui/material";
import { CustomTextField } from "~components/atoms";
import { useStreamContext } from "~contexts/Stream";
import { authFetch } from "~helpers/authFetch";
import { useToastNotificationContext } from "~contexts/ToastNotification";

// Componente de input seguindo o padrão de ConfigsGerais
interface TextInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  theme: any;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
}

// Componente de item de ícone
interface IconItemProps {
  iconKey: string;
  iconName: string;
  iconUrl: string;
  menuName: string;
  onIconUrlChange: (value: string) => void;
  onMenuNameChange: (value: string) => void;
  theme: any;
  defaultIconUrl?: string;
}

const IconItem: React.FC<IconItemProps> = ({
  iconKey,
  iconName,
  iconUrl,
  menuName,
  onIconUrlChange,
  onMenuNameChange,
  theme,
  defaultIconUrl,
}) => {
  // Se há URL customizada, usar ela; senão, usar o padrão
  const displayIconUrl = iconUrl || defaultIconUrl || "";
  // Fundo branco apenas quando há URL customizada (diferente do padrão)
  const hasCustomIcon = !!iconUrl;

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        width: "calc(50% - 8px)",
        minWidth: 0,
        flexShrink: 0,
      }}
    >
      {/* Preview do ícone */}
      <Box
        sx={{
          width: "88px",
          height: "125px",
          borderRadius: "8px",
          backgroundColor: hasCustomIcon ? "#FFFFFF" : theme.palette.grey[700],
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          padding: "8px",
        }}
      >
        {displayIconUrl ? (
          <Box
            component="img"
            src={displayIconUrl}
            alt={iconKey}
            sx={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
            }}
            onError={(e) => {
              // Se o ícone não carregar, mostrar um placeholder
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        ) : (
          <Box
            sx={{
              width: "40px",
              height: "40px",
              border: `2px dashed ${theme.palette.grey[500]}`,
              borderRadius: "4px",
            }}
          />
        )}
      </Box>

      {/* Inputs */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          justifyContent: "center",
        }}
      >
        {/* Nome do ícone */}
        <Typography
          sx={{
            color: theme.palette.grey[0],
            fontSize: "13px",
            fontWeight: 400,
            mb: 0,
            lineHeight: "16px",
          }}
        >
          {iconName}
        </Typography>

        <CustomTextField
          value={iconUrl}
          onChange={(e) => onIconUrlChange(e.target.value)}
          label="link do ícone"
          fullWidth
          height="44px"
          labelFontSize="13px"
          labelTransformDefault="translate(16px, 13px) scale(1)"
          sx={{
            width: "100%",
            maxWidth: "244px",
          }}
        />

        <CustomTextField
          value={menuName}
          onChange={(e) => onMenuNameChange(e.target.value)}
          label="nome no menu"
          fullWidth
          height="44px"
          labelFontSize="13px"
          labelTransformDefault="translate(16px, 13px) scale(1)"
          sx={{
            width: "100%",
            maxWidth: "244px",
          }}
        />
      </Box>
    </Box>
  );
};

// Componente de item de ícone para "demais ícones" (apenas 1 input)
interface OtherIconItemProps {
  iconKey: string;
  iconName: string;
  description: string;
  iconUrl: string;
  onIconUrlChange: (value: string) => void;
  theme: any;
  defaultIconUrl?: string;
}

const OtherIconItem: React.FC<OtherIconItemProps> = ({
  iconKey,
  iconName,
  description,
  iconUrl,
  onIconUrlChange,
  theme,
  defaultIconUrl,
}) => {
  const displayIconUrl = iconUrl || defaultIconUrl || "";
  const hasCustomIcon = !!iconUrl;

  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
        width: "calc(50% - 8px)",
        minWidth: 0,
        flexShrink: 0,
      }}
    >
      {/* Preview do ícone */}
      <Box
        sx={{
          width: "88px",
          height: "125px",
          borderRadius: "8px",
          backgroundColor: hasCustomIcon ? "#FFFFFF" : theme.palette.grey[700],
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          padding: "8px",
        }}
      >
        {displayIconUrl ? (
          <Box
            component="img"
            src={displayIconUrl}
            alt={iconKey}
            sx={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        ) : (
          <Box
            sx={{
              width: "40px",
              height: "40px",
              border: `2px dashed ${theme.palette.grey[500]}`,
              borderRadius: "4px",
            }}
          />
        )}
      </Box>

      {/* Input */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 0,
          justifyContent: "center",
        }}
      >
        {/* Nome do ícone */}
        <Typography
          sx={{
            color: theme.palette.grey[0],
            fontSize: "13px",
            fontWeight: 400,
            mb: 0,
            lineHeight: "16px",
          }}
        >
          {iconName}
        </Typography>

        {/* Descrição */}
        <Typography
          sx={{
            color: "#858585",
            fontSize: "11px",
            fontWeight: 400,
            mb: 1,
            lineHeight: "16px",
          }}
        >
          {description}
        </Typography>

        <CustomTextField
          value={iconUrl}
          onChange={(e) => onIconUrlChange(e.target.value)}
          label="link do ícone"
          fullWidth
          height="44px"
          labelFontSize="13px"
          labelTransformDefault="translate(16px, 13px) scale(1)"
          sx={{
            width: "100%",
            maxWidth: "244px",
          }}
        />
      </Box>
    </Box>
  );
};

const TextInput: React.FC<TextInputProps> = ({
  label,
  value,
  onChange,
  theme,
  placeholder,
  multiline = false,
  rows = 1,
}) => {
  return (
    <CustomTextField
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      fullWidth
      multiline={multiline}
      rows={multiline ? rows : undefined}
      height={multiline ? "auto" : "56px"}
      sx={{
        "& .MuiInputBase-input": {
          padding: multiline ? "16px" : "0 16px",
        },
      }}
    />
  );
};

// Interface para dados do rich link
interface RichLinkData {
  ogImage?: string;
  ogTitle?: string;
  ogDescription?: string;
  siteFaviconUrl?: string;
}

const DEFAULT_MENU_ICONS: Record<string, { iconUrl: string; menuName: string }> = {
  produtos: { iconUrl: "", menuName: "" },
  sacola: { iconUrl: "", menuName: "" },
  enquete: { iconUrl: "", menuName: "" },
  ajuda: { iconUrl: "", menuName: "" },
  medidas: { iconUrl: "", menuName: "" },
  lead_capture: { iconUrl: "", menuName: "" },
};

const DEFAULT_OTHER_ICONS: Record<string, string> = {
  busca: "",
  fechar: "",
  setaDireita: "",
  setaEsquerda: "",
  setaCima: "",
  setaBaixo: "",
  remover: "",
  adicionar: "",
  excluir: "",
  resultadosEnquete: "",
  somAtivo: "",
  somInativo: "",
  chat: "",
  envioMensagem: "",
  feedbackPositivo: "",
  compartilhar: "",
};

const TextoEIcons: React.FC = () => {
  const theme: any = useTheme();
  const { currentStream } = useStreamContext();
  const { setToastData } = useToastNotificationContext();

  // Estados para textos do chat
  const [chatText, setChatText] = useState("");

  // Estados para textos de ajuda
  const [helpText, setHelpText] = useState("");
  const [leadCaptureSubtitle, setLeadCaptureSubtitle] = useState("");
  const [leadCaptureSuccessText, setLeadCaptureSuccessText] = useState("");
  const [leadCaptureEnabled, setLeadCaptureEnabled] = useState(true);

  // Estados para rich link
  const [richLinkData, setRichLinkData] = useState<RichLinkData | null>(null);
  const [richLinkEditable, setRichLinkEditable] = useState<RichLinkData>({
    ogImage: "",
    ogTitle: "",
    ogDescription: "",
    siteFaviconUrl: "",
  });
  const [loadingRichLink, setLoadingRichLink] = useState(false);

  // Estados para ícones do menu
  const [menuIcons, setMenuIcons] =
    useState<Record<string, { iconUrl: string; menuName: string }>>(DEFAULT_MENU_ICONS);

  // Estados para demais ícones
  const [otherIcons, setOtherIcons] =
    useState<Record<string, string>>(DEFAULT_OTHER_ICONS);

  // Estados gerais
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Função para buscar dados do rich link
  const fetchRichLinkData = useCallback(async () => {
    if (!currentStream?.id) {
      return;
    }

    setLoadingRichLink(true);
    try {
      const response = await authFetch({
        path: `${process.env.REACT_APP_BACKEND_URL}/texts-icons/${currentStream.id}/rich-link`,
        method: "GET",
      });

      if (response.ok) {
        const data = await response.json();
        setRichLinkData({
          ogImage: data.ogImage || "",
          ogTitle: data.ogTitle || "",
          ogDescription: data.ogDescription || "",
        });
      } else {
        setRichLinkData(null);
      }
    } catch (error) {
      console.error("Error fetching rich link data:", error);
      setRichLinkData(null);
    } finally {
      setLoadingRichLink(false);
    }
  }, [currentStream?.id]);

  // Função para carregar textos do backend
  const loadTextsData = useCallback(async () => {
    if (!currentStream?.id) {
      return;
    }

    setLoading(true);
    setChatText("");
    setHelpText("");
    setMenuIcons(DEFAULT_MENU_ICONS);
    setOtherIcons(DEFAULT_OTHER_ICONS);
    setRichLinkEditable({
      ogImage: "",
      ogTitle: "",
      ogDescription: "",
      siteFaviconUrl: "",
    });
    try {
      const response = await authFetch({
        path: `${process.env.REACT_APP_BACKEND_URL}/texts-icons/${currentStream.id}`,
        method: "GET",
      });

      if (response.ok) {
        const data = await response.json();
        setChatText(data.chat_text || "");
        // Limpar possíveis quebras de linha extras e espaços que o editor pode ter salvo
        // Remove quebras de linha e espaços entre tags que causam acumulo de linhas em branco
        let loadedHelpText = (data.help_text || "");
        loadedHelpText = loadedHelpText.replace(/[\r\n]+/g, "");
        loadedHelpText = loadedHelpText.replace(/>\s+</g, "><");
        // Remove parágrafos vazios explícitos que podem ter sido salvos ou gerados anteriormente
        loadedHelpText = loadedHelpText.replace(/<p><br><\/p>/g, "");
        loadedHelpText = loadedHelpText.replace(/<p>\s*<\/p>/g, "");
        loadedHelpText = loadedHelpText.replace(/<p>&nbsp;<\/p>/g, "");
        setHelpText(loadedHelpText === "<p><br></p>" ? "" : loadedHelpText);
        setLeadCaptureSubtitle(data.lead_capture_subtitle || "");
        setLeadCaptureSuccessText(data.lead_capture_success_text || "");
        setLeadCaptureEnabled(data.lead_capture_enabled !== undefined ? data.lead_capture_enabled : true);

        // Carregar ícones do menu
        setMenuIcons(() => {
          const updated = { ...DEFAULT_MENU_ICONS };
          if (data.menu_icons) {
            Object.keys(updated).forEach((key) => {
              if (data.menu_icons[key]) {
                updated[key] = {
                  iconUrl: data.menu_icons[key].iconUrl || "",
                  menuName: data.menu_icons[key].menuName || "",
                };
              }
            });
          }
          return updated;
        });

        // Carregar demais ícones
        setOtherIcons(() => {
          const updated = { ...DEFAULT_OTHER_ICONS };
          if (data.other_icons) {
            Object.keys(updated).forEach((key) => {
              if (data.other_icons[key] !== undefined) {
                updated[key] = data.other_icons[key] || "";
              }
            });
          }
          return updated;
        });

        // Carregar dados do rich link
        if (data.rich_link) {
          setRichLinkEditable({
            ogImage: data.rich_link.ogImage || "",
            ogTitle: data.rich_link.ogTitle || "",
            ogDescription: data.rich_link.ogDescription || "",
            siteFaviconUrl:
              data.rich_link.siteFaviconUrl ||
              data.rich_link.site_favicon_url ||
              data.rich_link.faviconUrl ||
              data.rich_link.favicon_url ||
              "",
          });
        }
      } else {
        setChatText("");
        setHelpText("");
        setLeadCaptureSubtitle("");
        setLeadCaptureSuccessText("");
        setLeadCaptureEnabled(true);
        setMenuIcons(DEFAULT_MENU_ICONS);
        setOtherIcons(DEFAULT_OTHER_ICONS);
      }
    } catch (error) {
      console.error("Error loading texts:", error);
      setChatText("");
      setHelpText("");
      setLeadCaptureSubtitle("");
      setLeadCaptureSuccessText("");
      setLeadCaptureEnabled(true);
      setMenuIcons(DEFAULT_MENU_ICONS);
      setOtherIcons(DEFAULT_OTHER_ICONS);
      setRichLinkEditable({
        ogImage: "",
        ogTitle: "",
        ogDescription: "",
        siteFaviconUrl: "",
      });
    } finally {
      setLoading(false);
    }
  }, [currentStream?.id]);

  // Carregar dados quando a stream mudar
  useEffect(() => {
    if (currentStream?.id) {
      loadTextsData();
      fetchRichLinkData();
    }
  }, [currentStream?.id, fetchRichLinkData, loadTextsData]);

  // Função para salvar textos
  const handleSave = useCallback(async () => {
    if (!currentStream?.id) {
      setToastData(
        {
          title: "Erro",
          text: "Nenhuma stream selecionada",
        },
        "error"
      );
      return;
    }

    try {
      setSaving(true);
      // Limpar possíveis quebras de linha fantasmas no final do HTML antes de salvar
      // Remove parágrafos vazios ou apenas com br no final e todas as quebras de linha
      let sanitizedHelpText = helpText.replace(/[\r\n]+/g, "");
      sanitizedHelpText = sanitizedHelpText.replace(/(<p><br><\/p>)+$/, "").trim();

      const response = await authFetch({
        path: `${process.env.REACT_APP_BACKEND_URL}/texts-icons/${currentStream.id}`,
        method: "PATCH",
        data: {
          chat_text: chatText,
          help_text: sanitizedHelpText,
          lead_capture_subtitle: leadCaptureSubtitle,
          lead_capture_success_text: leadCaptureSuccessText,
          lead_capture_enabled: leadCaptureEnabled,
          menu_icons: menuIcons,
          other_icons: otherIcons,
          rich_link: richLinkEditable,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || "Erro ao salvar textos");
        (error as any).status = response.status;
        throw error;
      }

      setToastData(
        {
          title: "Sucesso",
          text: "Textos salvos com sucesso",
        },
        "success"
      );
    } catch (error: any) {
      console.error("Error saving texts:", error);
      let errorMessage = "Erro ao salvar textos";

      if (error.status === 400) {
        errorMessage = error.message || "Dados inválidos";
      }

      setToastData(
        {
          title: "Erro",
          text: errorMessage,
        },
        "error"
      );
    } finally {
      setSaving(false);
    }
  }, [
    currentStream?.id,
    chatText,
    helpText,
    leadCaptureSubtitle,
    leadCaptureSuccessText,
    leadCaptureEnabled,
    menuIcons,
    otherIcons,
    richLinkEditable,
    setToastData,
  ]);

  if (!currentStream) {
    return (
      <Box>
        <Typography
          variant="h4"
          sx={{ color: theme.palette.grey[0], mb: 0.5, fontSize: "30px !important" }}
        >
          texto e ícones
        </Typography>
        <Typography variant="body2" sx={{ color: "#858585", fontSize: "15px" }}>
          Selecione uma marca para editar os textos e ícones
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ color: theme.palette.grey[0], mb: 0.5, fontSize: "30px !important" }}
          >
            texto e ícones
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#858585", mb: "40px", fontSize: "15px" }}
          >
            gerencie textos e ícones da transmissão
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading}
          sx={{
            backgroundColor: "#FFFFFF",
            color: "#000000",
            borderRadius: "100px",
            paddingTop: "16px",
            paddingRight: "24px",
            paddingBottom: "16px",
            paddingLeft: "24px",
            height: "48px",
            gap: "12px",
            fontWeight: 400,
            fontSize: "11px",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#F5F5F5",
            },
            "&:disabled": {
              backgroundColor: "#666666",
              color: "#FFFFFF",
            },
          }}
        >
          {saving ? (
            <>
              <CircularProgress size={16} sx={{ color: "#000000" }} />
              salvando...
            </>
          ) : (
            <>
              publicar
              <Box
                component="img"
                src="/icons/publish.svg"
                alt="publicar"
                sx={{
                  width: "24px",
                  height: "24px",
                }}
              />
            </>
          )}
        </Button>
      </Box>

      <Box sx={{ maxWidth: "720px" }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "400px",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Textos do Chat */}
            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  color: theme.palette.grey[0],
                  fontSize: "23px",
                  fontWeight: 400,
                  mb: 0,
                }}
              >
                texto do chat
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#858585",
                  fontSize: "11px",
                  mb: 2,
                }}
              >
                aparece junto aos campos de nome e e-mail antes de liberar o chat
              </Typography>
              <TextInput
                label=""
                value={chatText}
                onChange={setChatText}
                theme={theme}
                placeholder="texto do chat"
              />
            </Box>

            {/* Textos de Ajuda */}
            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  color: theme.palette.grey[0],
                  fontSize: "23px",
                  fontWeight: 400,
                  mb: 0,
                }}
              >
                texto de ajuda
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#858585",
                  fontSize: "11px",
                  mb: 2,
                }}
              >
                dentro da modal de ajuda, campo aceita texto em html
              </Typography>
              <Box
                sx={{
                  border: `1px solid ${theme.palette.grey[700]}`,
                  borderRadius: "8px",
                  overflow: "hidden",
                  "& .ql-toolbar": {
                    border: "none",
                    borderBottom: `1px solid ${theme.palette.grey[700]}`,
                    backgroundColor: theme.palette.grey[900],
                    color: theme.palette.grey[0],
                  },
                  "& .ql-container": {
                    border: "none",
                    color: theme.palette.grey[0],
                  },
                  "& .ql-editor": {
                    minHeight: "320px",
                    color: theme.palette.grey[0],
                  },
                  "& .ql-toolbar .ql-picker": {
                    color: theme.palette.grey[0],
                  },
                  "& .ql-toolbar .ql-picker-item": {
                    backgroundColor: theme.palette.grey[900],
                  },
                  "& .ql-toolbar .ql-picker-options": {
                    backgroundColor: theme.palette.grey[900],
                  },
                  "& .ql-toolbar .ql-stroke": {
                    stroke: theme.palette.grey[0],
                  },
                  "& .ql-toolbar .ql-fill": {
                    fill: theme.palette.grey[0],
                  },
                }}
              >
                <ReactQuill
                  theme="snow"
                  value={helpText}
                  onChange={(content) => {
                    // Prevenir o acúmulo de quebras de linha que o Quill às vezes gera
                    if (content === "<p><br></p>") {
                      setHelpText("");
                    } else {
                      setHelpText(content);
                    }
                  }}
                  placeholder="texto de ajuda"
                  modules={{
                    toolbar: [
                      [{ header: [1, 2, 3, false] }],
                      ["bold", "italic", "underline", "strike"],
                      [{ list: "ordered" }, { list: "bullet" }],
                      [{ script: "sub" }, { script: "super" }],
                      [{ indent: "-1" }, { indent: "+1" }],
                      ["link", "image"],
                      [{ color: [] }, { background: [] }],
                      [{ align: [] }],
                      ["clean"],
                      ["code-block"],
                    ],
                  }}
                  formats={[
                    "header",
                    "bold",
                    "italic",
                    "underline",
                    "strike",
                    "list",
                    "bullet",
                    "script",
                    "indent",
                    "link",
                    "image",
                    "color",
                    "background",
                    "align",
                    "code-block",
                  ]}
                />
              </Box>
            </Box>

            {/* Lead Capture Texts */}
            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: theme.palette.grey[0],
                    fontSize: "23px",
                    fontWeight: 400,
                    mb: 0,
                  }}
                >
                  captação de lead
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography
                    sx={{
                      color: theme.palette.grey[0],
                      fontSize: "15px",
                      fontWeight: 400,
                    }}
                  >
                    ativar componente
                  </Typography>
                  <Switch
                    checked={leadCaptureEnabled}
                    onChange={(e) => setLeadCaptureEnabled(e.target.checked)}
                    sx={{
                      width: "40px",
                      height: "24px",
                    }}
                  />
                </Box>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: "#858585",
                  fontSize: "11px",
                  mb: 2,
                }}
              >
                textos para o formulário de captação de lead (título e nome do botão são configurados no ícone do menu)
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextInput
                  label="subtítulo"
                  value={leadCaptureSubtitle}
                  onChange={setLeadCaptureSubtitle}
                  theme={theme}
                  placeholder="ex: receba nossas novidades em primeira mão"
                />
                
                 <TextInput
                  label="mensagem de sucesso"
                  value={leadCaptureSuccessText}
                  onChange={setLeadCaptureSuccessText}
                  theme={theme}
                  placeholder="ex: dados enviados com sucesso!"
                />
              </Box>
            </Box>

            {/* Preview do Rich Link */}
            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  color: theme.palette.grey[0],
                  fontSize: "23px",
                  fontWeight: 400,
                  mb: 0,
                }}
              >
                preview do rich link
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#858585",
                  fontSize: "11px",
                  mb: 2,
                }}
              >
                apenas visualização, para editar essa mensagem é necessário entrar em
                contato com a equipe de produto
              </Typography>

              {loadingRichLink ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "200px",
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : richLinkData ? (
                <Box
                  sx={{
                    position: "relative",
                    borderRadius: "8px",
                    backgroundColor: theme.palette.grey[900],
                    overflow: "hidden",
                    maxWidth: "350px",
                    width: "100%",
                  }}
                >
                  {richLinkData.ogImage && (
                    <Box
                      component="img"
                      src={richLinkData.ogImage}
                      alt="Rich link preview"
                      sx={{
                        width: "100%",
                        display: "block",
                        objectFit: "cover",
                        maxHeight: "200px",
                        aspectRatio: "16/9",
                      }}
                    />
                  )}
                  <Box sx={{ p: "12px" }}>
                    {richLinkData.ogTitle && (
                      <Typography
                        variant="h6"
                        sx={{
                          color: theme.palette.grey[0],
                          mb: 0.5,
                          fontSize: "14px",
                          fontWeight: 600,
                          lineHeight: 1.3,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {richLinkData.ogTitle}
                      </Typography>
                    )}
                    {richLinkData.ogDescription && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.grey[400],
                          fontSize: "12px",
                          lineHeight: 1.4,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {richLinkData.ogDescription}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    borderRadius: "8px",
                    backgroundColor: theme.palette.grey[900],
                    p: 2,
                    textAlign: "center",
                    color: theme.palette.grey[400],
                    maxWidth: "350px",
                    width: "100%",
                  }}
                >
                  Não foi possível carregar o preview do rich link
                </Box>
              )}
            </Box>

            {/* Edição do Rich Link */}
            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  color: theme.palette.grey[0],
                  fontSize: "23px",
                  fontWeight: 400,
                  mb: 0,
                }}
              >
                edição do rich link
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#858585",
                  fontSize: "11px",
                  mb: 2,
                }}
              >
                altere as informações de compartilhamento para quando o link for enviado
                via redes sociais ou whatsapp
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {/* Preview da imagem manual */}
                <Box
                  sx={{
                    borderRadius: "8px",
                    overflow: "hidden",
                    backgroundColor: theme.palette.grey[900],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    maxWidth: "350px",
                    aspectRatio: "16/9",
                    maxHeight: "200px",
                    border: `1px solid ${theme.palette.grey[700]}`,
                  }}
                >
                  {richLinkEditable.ogImage ? (
                    <Box
                      component="img"
                      src={richLinkEditable.ogImage}
                      alt="Preview RICH LINK"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  ) : (
                    <Typography sx={{ color: theme.palette.grey[400], fontSize: "12px" }}>
                      sem imagem
                    </Typography>
                  )}
                </Box>

                <CustomTextField
                  label="url da imagem de compartilhamento"
                  value={richLinkEditable.ogImage}
                  onChange={(e) =>
                    setRichLinkEditable((prev) => ({ ...prev, ogImage: e.target.value }))
                  }
                  fullWidth
                />
                <CustomTextField
                  label="título de compartilhamento"
                  value={richLinkEditable.ogTitle}
                  onChange={(e) =>
                    setRichLinkEditable((prev) => ({ ...prev, ogTitle: e.target.value }))
                  }
                  fullWidth
                />
                <CustomTextField
                  label="descrição de compartilhamento"
                  value={richLinkEditable.ogDescription}
                  onChange={(e) =>
                    setRichLinkEditable((prev) => ({
                      ...prev,
                      ogDescription: e.target.value,
                    }))
                  }
                  fullWidth
                  multiline
                  rows={2}
                  height="auto"
                />
              </Box>
            </Box>

            {/* Seção de Ícones */}
            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  color: theme.palette.grey[0],
                  fontSize: "23px",
                  fontWeight: 400,
                  mb: 0,
                }}
              >
                favicon do site
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#858585",
                  fontSize: "11px",
                  mb: 2,
                }}
              >
                url do favicon do site
              </Typography>

              <CustomTextField
                label="url do favicon"
                value={richLinkEditable.siteFaviconUrl || ""}
                onChange={(e) =>
                  setRichLinkEditable((prev) => ({ ...prev, siteFaviconUrl: e.target.value }))
                }
                fullWidth
                InputProps={{
                  startAdornment: richLinkEditable.siteFaviconUrl ? (
                    <InputAdornment position="start">
                      <Box
                        sx={{
                          width: "24px",
                          height: "24px",
                          borderRadius: "6px",
                          backgroundColor: "#FFFFFF",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          component="img"
                          src={richLinkEditable.siteFaviconUrl}
                          alt="favicon do site"
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = "none";
                          }}
                        />
                      </Box>
                    </InputAdornment>
                  ) : undefined,
                }}
              />
            </Box>

            {/* Seção de Ícones */}
            <Box sx={{ mt: "56px" }}>
              <Typography
                variant="subtitle1"
                sx={{
                  color: theme.palette.grey[0],
                  fontSize: "23px",
                  fontWeight: 400,
                  mb: 0,
                }}
              >
                ícones
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#858585",
                  fontSize: "11px",
                  mb: 3,
                }}
              >
                link de cada ícone em .svg
              </Typography>



            {/* Itens do Menu */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: theme.palette.grey[0],
                    fontSize: "23px",
                    fontWeight: 400,
                    mb: 2,
                  }}
                >
                  itens do menu
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  <IconItem
                    iconKey="produtos"
                    iconName="produtos"
                    iconUrl={menuIcons.produtos.iconUrl}
                    menuName={menuIcons.produtos.menuName}
                    onIconUrlChange={(value) =>
                      setMenuIcons((prev) => ({
                        ...prev,
                        produtos: { ...prev.produtos, iconUrl: value },
                      }))
                    }
                    onMenuNameChange={(value) =>
                      setMenuIcons((prev) => ({
                        ...prev,
                        produtos: { ...prev.produtos, menuName: value },
                      }))
                    }
                    theme={theme}
                    defaultIconUrl="/icons/frontend-default/produtos.png"
                  />
                  <IconItem
                    iconKey="sacola"
                    iconName="sacola"
                    iconUrl={menuIcons.sacola.iconUrl}
                    menuName={menuIcons.sacola.menuName}
                    onIconUrlChange={(value) =>
                      setMenuIcons((prev) => ({
                        ...prev,
                        sacola: { ...prev.sacola, iconUrl: value },
                      }))
                    }
                    onMenuNameChange={(value) =>
                      setMenuIcons((prev) => ({
                        ...prev,
                        sacola: { ...prev.sacola, menuName: value },
                      }))
                    }
                    theme={theme}
                    defaultIconUrl="/icons/frontend-default/sacola.png"
                  />
                  <IconItem
                    iconKey="lead_capture"
                    iconName="lead_capture"
                    iconUrl={menuIcons.lead_capture.iconUrl}
                    menuName={menuIcons.lead_capture.menuName}
                    onIconUrlChange={(value) =>
                      setMenuIcons((prev) => ({
                        ...prev,
                        lead_capture: { ...prev.lead_capture, iconUrl: value },
                      }))
                    }
                    onMenuNameChange={(value) =>
                      setMenuIcons((prev) => ({
                        ...prev,
                        lead_capture: { ...prev.lead_capture, menuName: value },
                      }))
                    }
                    theme={theme}
                    defaultIconUrl="/icons/frontend-default/produtos.png" // Using a generic default for now
                  />
                  <IconItem
                    iconKey="enquete"
                    iconName="enquete"
                    iconUrl={menuIcons.enquete.iconUrl}
                    menuName={menuIcons.enquete.menuName}
                    onIconUrlChange={(value) =>
                      setMenuIcons((prev) => ({
                        ...prev,
                        enquete: { ...prev.enquete, iconUrl: value },
                      }))
                    }
                    onMenuNameChange={(value) =>
                      setMenuIcons((prev) => ({
                        ...prev,
                        enquete: { ...prev.enquete, menuName: value },
                      }))
                    }
                    theme={theme}
                    defaultIconUrl="/icons/frontend-default/esquete.png"
                  />
                  <IconItem
                    iconKey="ajuda"
                    iconName="ajuda"
                    iconUrl={menuIcons.ajuda.iconUrl}
                    menuName={menuIcons.ajuda.menuName}
                    onIconUrlChange={(value) =>
                      setMenuIcons((prev) => ({
                        ...prev,
                        ajuda: { ...prev.ajuda, iconUrl: value },
                      }))
                    }
                    onMenuNameChange={(value) =>
                      setMenuIcons((prev) => ({
                        ...prev,
                        ajuda: { ...prev.ajuda, menuName: value },
                      }))
                    }
                    theme={theme}
                    defaultIconUrl="/icons/frontend-default/ajuda.png"
                  />
                  <IconItem
                    iconKey="medidas"
                    iconName="medidas (do apresentador)"
                    iconUrl={menuIcons.medidas.iconUrl}
                    menuName={menuIcons.medidas.menuName}
                    onIconUrlChange={(value) =>
                      setMenuIcons((prev) => ({
                        ...prev,
                        medidas: { ...prev.medidas, iconUrl: value },
                      }))
                    }
                    onMenuNameChange={(value) =>
                      setMenuIcons((prev) => ({
                        ...prev,
                        medidas: { ...prev.medidas, menuName: value },
                      }))
                    }
                    theme={theme}
                    defaultIconUrl="/icons/frontend-default/medidas.png"
                  />
                </Box>
              </Box>

              {/* Demais Ícones */}
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: theme.palette.grey[0],
                    fontSize: "23px",
                    fontWeight: 400,
                    mb: 2,
                  }}
                >
                  demais ícones
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  <OtherIconItem
                    iconKey="busca"
                    iconName="busca"
                    description="lista de produtos"
                    iconUrl={otherIcons.busca}
                    onIconUrlChange={(value) =>
                      setOtherIcons((prev) => ({ ...prev, busca: value }))
                    }
                    theme={theme}
                    defaultIconUrl="/icons/frontend-default/busca.png"
                  />
                  <OtherIconItem
                    iconKey="fechar"
                    iconName="fechar"
                    description="diversas modais"
                    iconUrl={otherIcons.fechar}
                    onIconUrlChange={(value) =>
                      setOtherIcons((prev) => ({ ...prev, fechar: value }))
                    }
                    theme={theme}
                    defaultIconUrl="/icons/frontend-default/fechar.png"
                  />
                  <OtherIconItem
                    iconKey="setaDireita"
                    iconName="seta direita"
                    description="diversos usos"
                    iconUrl={otherIcons.setaDireita}
                    onIconUrlChange={(value) =>
                      setOtherIcons((prev) => ({ ...prev, setaDireita: value }))
                    }
                    theme={theme}
                    defaultIconUrl="/icons/frontend-default/seta-direita.png"
                  />
                  <OtherIconItem
                    iconKey="setaEsquerda"
                    iconName="seta esquerda"
                    description="diversos usos"
                    iconUrl={otherIcons.setaEsquerda}
                    onIconUrlChange={(value) =>
                      setOtherIcons((prev) => ({ ...prev, setaEsquerda: value }))
                    }
                    theme={theme}
                    defaultIconUrl="/icons/frontend-default/seta-esquerda.png"
                  />
                  <OtherIconItem
                    iconKey="setaCima"
                    iconName="seta cima"
                    description="diversos usos"
                    iconUrl={otherIcons.setaCima}
                    onIconUrlChange={(value) =>
                      setOtherIcons((prev) => ({ ...prev, setaCima: value }))
                    }
                    theme={theme}
                    defaultIconUrl="/icons/frontend-default/seta-cima.png"
                  />
                  <OtherIconItem
                    iconKey="setaBaixo"
                    iconName="seta baixo"
                    description="diversos usos"
                    iconUrl={otherIcons.setaBaixo}
                    onIconUrlChange={(value) =>
                      setOtherIcons((prev) => ({ ...prev, setaBaixo: value }))
                    }
                    theme={theme}
                    defaultIconUrl="/icons/frontend-default/seta-baixo.png"
                  />
                  <OtherIconItem
                    iconKey="remover"
                    iconName="remover"
                    description="reduzir quantidade de itens"
                    iconUrl={otherIcons.remover}
                    onIconUrlChange={(value) =>
                      setOtherIcons((prev) => ({ ...prev, remover: value }))
                    }
                    theme={theme}
                    defaultIconUrl="/icons/frontend-default/remover.png"
                  />
                  <OtherIconItem
                    iconKey="adicionar"
                    iconName="adicionar"
                    description="adicionar itens"
                    iconUrl={otherIcons.adicionar}
                    onIconUrlChange={(value) =>
                      setOtherIcons((prev) => ({ ...prev, adicionar: value }))
                    }
                    theme={theme}
                    defaultIconUrl="/icons/frontend-default/adicionar.png"
                  />
                  <OtherIconItem
                    iconKey="excluir"
                    iconName="excluir"
                    description="excluir itens"
                    iconUrl={otherIcons.excluir}
                    onIconUrlChange={(value) =>
                      setOtherIcons((prev) => ({ ...prev, excluir: value }))
                    }
                    theme={theme}
                    defaultIconUrl="/icons/frontend-default/excluir.png"
                  />
                  <OtherIconItem
                    iconKey="resultadosEnquete"
                    iconName="resultados da enquete"
                    description="tab de resultados em enquete"
                    iconUrl={otherIcons.resultadosEnquete}
                    onIconUrlChange={(value) =>
                      setOtherIcons((prev) => ({ ...prev, resultadosEnquete: value }))
                    }
                    theme={theme}
                    defaultIconUrl="/icons/frontend-default/resultados-da-enquete.png"
                  />
                  <OtherIconItem
                    iconKey="somAtivo"
                    iconName="som ativo"
                    description="controles do vídeo"
                    iconUrl={otherIcons.somAtivo}
                    onIconUrlChange={(value) =>
                      setOtherIcons((prev) => ({ ...prev, somAtivo: value }))
                    }
                    theme={theme}
                    defaultIconUrl="/icons/frontend-default/som-ativo.png"
                  />
                  <OtherIconItem
                    iconKey="somInativo"
                    iconName="som inativo"
                    description="controles do vídeo"
                    iconUrl={otherIcons.somInativo}
                    onIconUrlChange={(value) =>
                      setOtherIcons((prev) => ({ ...prev, somInativo: value }))
                    }
                    theme={theme}
                    defaultIconUrl="/icons/frontend-default/som-inativo.png"
                  />
                  <OtherIconItem
                    iconKey="chat"
                    iconName="chat"
                    description="controles do vídeo"
                    iconUrl={otherIcons.chat}
                    onIconUrlChange={(value) =>
                      setOtherIcons((prev) => ({ ...prev, chat: value }))
                    }
                    theme={theme}
                    defaultIconUrl="/icons/frontend-default/chat.png"
                  />
                  <OtherIconItem
                    iconKey="envioMensagem"
                    iconName="envio de mensagem"
                    description="campo do chat"
                    iconUrl={otherIcons.envioMensagem}
                    onIconUrlChange={(value) =>
                      setOtherIcons((prev) => ({ ...prev, envioMensagem: value }))
                    }
                    theme={theme}
                    defaultIconUrl="/icons/frontend-default/envio-de-mensagem.png"
                  />
                  <OtherIconItem
                    iconKey="feedbackPositivo"
                    iconName="feedback positivo"
                    description="feedback positivo"
                    iconUrl={otherIcons.feedbackPositivo}
                    onIconUrlChange={(value) =>
                      setOtherIcons((prev) => ({ ...prev, feedbackPositivo: value }))
                    }
                    theme={theme}
                    defaultIconUrl="/icons/frontend-default/feedback-positivo.png"
                  />
                  <OtherIconItem
                    iconKey="compartilhar"
                    iconName="compartilhar"
                    description="diversos usos"
                    iconUrl={otherIcons.compartilhar}
                    onIconUrlChange={(value) =>
                      setOtherIcons((prev) => ({ ...prev, compartilhar: value }))
                    }
                    theme={theme}
                    defaultIconUrl="/icons/frontend-default/compartilhar.png"
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TextoEIcons;
